import { useMemo, useState } from "react";
import { addCalendarDays } from "@common/helpers/date";
import { capitalize } from "@common/helpers/string";
import { DensityChart } from "@design-system/DensityChart/DensityChart";
import type {
    DensityChartBar,
    DensityChartDay,
    DensityChartHighlight,
    DensityChartSeries,
    DensityChartSlice,
    DensityChartWindow,
} from "@design-system/DensityChart/DensityChart.lib";
import type { DensityChartBucketUnit } from "@design-system/DensityChart/densityBuckets.lib";
import {
    DENSITY_CHART_BUCKET_UNITS,
    listDates,
} from "@design-system/DensityChart/densityBuckets.lib";
import {
    BooleanControl,
    PlaygroundSelect,
    VariantGrid,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

type DataShape = "typical" | "huge-day" | "empty";
type RangeLength = "14" | "90" | "400" | "1500";
type LoadState = "ready" | "loading" | "fetching";
type HighlightShape = "none" | "range" | "kick" | "kick-in-range";

const dataShapeOptions = [
    "typical",
    "huge-day",
    "empty",
] satisfies readonly DataShape[];
const rangeLengthOptions = [
    "14",
    "90",
    "400",
    "1500",
] satisfies readonly RangeLength[];
const loadStateOptions = [
    "ready",
    "loading",
    "fetching",
] satisfies readonly LoadState[];
const highlightShapeOptions = [
    "none",
    "range",
    "kick",
    "kick-in-range",
] satisfies readonly HighlightShape[];

const END_DATE = "2026-10-14";
const DEFAULT_WINDOW: DensityChartWindow = {
    start: addCalendarDays(END_DATE, -6),
    end: END_DATE,
};
const HIGHLIGHTED_RANGE: DensityChartWindow = {
    start: addCalendarDays(END_DATE, -4),
    end: addCalendarDays(END_DATE, -2),
};
const KICK_SERIES_IDS: ReadonlySet<string> = new Set(["kick"]);
const HIGHLIGHTS: Record<HighlightShape, DensityChartHighlight | undefined> = {
    none: undefined,
    range: { range: HIGHLIGHTED_RANGE },
    kick: { seriesIds: KICK_SERIES_IDS },
    "kick-in-range": { range: HIGHLIGHTED_RANGE, seriesIds: KICK_SERIES_IDS },
};

const DEMO_SERIES: DensityChartSeries[] = [
    "people",
    "kick",
    "integrations",
    "rules",
].map((id) => ({
    id,
    label: capitalize(id),
    color: `var(--chart-series-${id})`,
    accentColor: `var(--chart-series-${id}-accent)`,
    selectedColor: `var(--chart-series-${id}-selected)`,
    hoverColor: `var(--chart-series-${id}-hover)`,
}));

const PATTERN = [12, 30, 8, 0, 44, 26, 18, 5, 60, 22];

/** Deterministic demo days so screenshots stay stable. */
function buildDemoDays(dates: string[], shape: DataShape): DensityChartDay[] {
    if (shape === "empty") {
        return [];
    }
    return dates.map((date, index) => {
        const base = PATTERN[index % PATTERN.length] ?? 0;
        const isHugeDay = shape === "huge-day" && index === dates.length - 6;
        return {
            date,
            values: {
                people: Math.round(base * 0.4),
                kick: isHugeDay ? 18_400 : base,
                integrations: isHugeDay ? 1_800 : Math.round(base * 0.6),
                rules: Math.round(base * 0.2),
            },
        };
    });
}

const workspaceStartMessage = (start: string) =>
    `Your activity starts on ${start}, when this workspace joined Kick.`;

interface BeforeStartExample {
    id: string;
    label: string;
    startDate: string;
    bucketUnit: DensityChartBucketUnit;
    beforeStartMessage: string;
    initialWindow?: DensityChartWindow;
}

const BEFORE_START_EXAMPLES: BeforeStartExample[] = [
    {
        id: "days",
        label: "a new workspace in days (history from Oct 5)",
        startDate: "2026-10-05",
        bucketUnit: "day",
        beforeStartMessage: workspaceStartMessage("Oct 5"),
        initialWindow: DEFAULT_WINDOW,
    },
    {
        id: "weeks",
        label: "a new workspace in weeks (history from Sep 16)",
        startDate: "2026-09-16",
        bucketUnit: "week",
        beforeStartMessage: workspaceStartMessage("Sep 16"),
    },
];

/** One zone example holding its own window; only days from the start have counts. */
function BeforeStartExampleChart({ example }: { example: BeforeStartExample }) {
    const { startDate, bucketUnit, beforeStartMessage, initialWindow } =
        example;
    const [window, setWindow] = useState(initialWindow);
    const days = useMemo(
        () => buildDemoDays(listDates(startDate, END_DATE), "typical"),
        [startDate],
    );
    return (
        <DensityChart
            ariaLabel={example.label}
            startDate={startDate}
            endDate={END_DATE}
            bucketUnit={bucketUnit}
            days={days}
            series={DEMO_SERIES}
            window={window}
            onWindowChange={window ? setWindow : undefined}
            beforeStartMessage={beforeStartMessage}
        />
    );
}

/**
 * Sandbox for DensityChart: data shapes, history lengths, day, week or
 * month bars, load states, with or without a window, highlighted days or
 * series drawn in each series' selected shade, and bar clicks reported or picking
 * the window. Below it, new workspaces whose
 * history fits, with the zone before the start, whose sentence shows on
 * hover.
 */
export const DensityChartPlayground = () => {
    const [dataShape, setDataShape] = useState<DataShape>("huge-day");
    const [rangeLength, setRangeLength] = useState<RangeLength>("400");
    const [bucketUnit, setBucketUnit] = useState<DensityChartBucketUnit>("day");
    const [loadState, setLoadState] = useState<LoadState>("ready");
    const [hasWindow, setHasWindow] = useState(true);
    const [window, setWindow] = useState(DEFAULT_WINDOW);
    const [highlightShape, setHighlightShape] =
        useState<HighlightShape>("none");
    const [hasBarClicks, setHasBarClicks] = useState(false);
    const [clickedSlice, setClickedSlice] = useState<DensityChartSlice>();
    const [clickedBar, setClickedBar] = useState<DensityChartBar>();
    const startDate = addCalendarDays(END_DATE, 1 - Number(rangeLength));
    const days = useMemo(
        () => buildDemoDays(listDates(startDate, END_DATE), dataShape),
        [startDate, dataShape],
    );

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Data</h2>
                        <PlaygroundSelect<DataShape>
                            options={dataShapeOptions}
                            value={dataShape}
                            onChange={setDataShape}
                        />
                    </section>
                    <section>
                        <h2>History days</h2>
                        <PlaygroundSelect<RangeLength>
                            options={rangeLengthOptions}
                            value={rangeLength}
                            onChange={setRangeLength}
                        />
                    </section>
                    <section>
                        <h2>Bars</h2>
                        <PlaygroundSelect<DensityChartBucketUnit>
                            options={DENSITY_CHART_BUCKET_UNITS}
                            value={bucketUnit}
                            onChange={setBucketUnit}
                        />
                    </section>
                    <section>
                        <h2>State</h2>
                        <PlaygroundSelect<LoadState>
                            options={loadStateOptions}
                            value={loadState}
                            onChange={setLoadState}
                        />
                    </section>
                    <section>
                        <BooleanControl
                            label="Window"
                            checked={hasWindow}
                            onChange={setHasWindow}
                        />
                        <BooleanControl
                            label="Bar clicks"
                            checked={hasBarClicks}
                            onChange={setHasBarClicks}
                        />
                    </section>
                    <section>
                        <h2>Highlight</h2>
                        <PlaygroundSelect<HighlightShape>
                            options={highlightShapeOptions}
                            value={highlightShape}
                            onChange={setHighlightShape}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={
                    hasWindow ? `${window.start} to ${window.end}` : "No window"
                }
                alignment="stretch"
                width="full"
                belowStage={BEFORE_START_EXAMPLES.map((example) => (
                    <VariantGrid
                        key={example.id}
                        title={`Before Kick zone, ${example.label}`}
                        columns="full"
                    >
                        <BeforeStartExampleChart example={example} />
                    </VariantGrid>
                ))}
                output={JSON.stringify(
                    { window, clickedSlice, clickedBar },
                    null,
                    2,
                )}
            >
                <DensityChart
                    ariaLabel="Density chart playground"
                    startDate={startDate}
                    endDate={END_DATE}
                    bucketUnit={bucketUnit}
                    days={days}
                    series={DEMO_SERIES}
                    window={hasWindow ? window : undefined}
                    onWindowChange={hasWindow ? setWindow : undefined}
                    onSliceClick={setClickedSlice}
                    onBarClick={hasBarClicks ? setClickedBar : undefined}
                    highlight={HIGHLIGHTS[highlightShape]}
                    isLoading={loadState === "loading"}
                    staleDays={
                        loadState === "fetching"
                            ? [{ start: startDate, end: END_DATE }]
                            : undefined
                    }
                />
            </ShowcasePreview>
        </SandboxLayout>
    );
};
